import { useMemo } from 'react';
import type { Workspace } from '@workspace/domain/workspace';
import { useCurrentUserId } from '@framework/auth';
import { useWorkspaceMemberRoles } from '@workspace/domain/workspace/useWorkspaceMemberRoles';
import { useViewModelIndividualPermissionEnabled } from '@workspace/domain/useViewModelIndividualPermissionEnabled';

type Return = {
  isMenuButtonShowable: boolean;
  isEditMenuButtonShowable: boolean;
  isAdminMenuButtonShowable: boolean;
  isEnabledIndividualPermissions: boolean;
};

export const useSidebarItemMenuPermissions = (workspace: Workspace): Return => {
  const currentUserId = useCurrentUserId();
  const roles = useWorkspaceMemberRoles(workspace.id);

  // ビューモデルの個別権限設定の表示可否フラグ
  const showConfigureIndividualPermissions = useViewModelIndividualPermissionEnabled(workspace.id);

  return useMemo(() => {
    if (!currentUserId) {
      return {
        isMenuButtonShowable: false,
        isEditMenuButtonShowable: false,
        isAdminMenuButtonShowable: false,
        isEnabledIndividualPermissions: false,
      };
    }

    const currentUserMemberRole = roles?.getRoleOf(currentUserId);

    const isAdminRole = currentUserMemberRole === 'admin';
    const isEditorRole = currentUserMemberRole === 'editor';

    return {
      isMenuButtonShowable: !!(currentUserId && currentUserMemberRole),
      isEditMenuButtonShowable: isAdminRole || isEditorRole,
      isAdminMenuButtonShowable: isAdminRole,
      isEnabledIndividualPermissions: (isAdminRole || isEditorRole) && showConfigureIndividualPermissions,
    };
  }, [currentUserId, roles, showConfigureIndividualPermissions]);
};
